import React, { useState, useEffect } from 'react';
import {
    Table,
    Header,
    Button,
    SpaceBetween,
    Box,
    Badge,
    Spinner,
    Alert,
} from '@cloudscape-design/components';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { OrganizationDetails } from 'types/organizationTypes';
import { Invitation } from 'types/Members';
import organizationService from 'services/organizationService';
import { useNotification } from 'contexts/NotificationContext';
import { useBreadcrumbs } from 'contexts/BreadcrumbContext';
import AppConfig from 'configs/AppConfig';

const PendingInvitationsPage = () => {
    const { addNotification } = useNotification();
    const { setBreadcrumbs } = useBreadcrumbs();
    const [loading, setLoading] = useState(true);
    const [loadingInvitations, setLoadingInvitations] = useState<Record<string, boolean>>({});
    const [invitations, setInvitations] = useState<Invitation[]>([]);
    const { id } = useParams<{ id: string }>();
    const [organization, setOrganization] = useState<OrganizationDetails | null>(null);
    const [error, setError] = useState<string | null>(null);

    const fetchInvitations = async () => {
        if (!id) return;
        try {
            setLoading(true);
            setError(null);
            const [orgData, invitationsData] = await Promise.all([
                organizationService.getOrganizationById(id),
                organizationService.getInvitations(id),
            ]);
            setOrganization(orgData);
            setInvitations(invitationsData || []);
        } catch (error) {
            console.error('Error fetching invitations:', error);
            setError('Failed to load invitations. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchInvitations();
    }, [id]);

    useEffect(() => {
        if (organization) {
            setBreadcrumbs([
                { text: AppConfig.APP_NAME, href: '/' },
                { text: 'Organizations', href: '/organizations' },
                { text: organization.display_name, href: `/organizations/${id}` },
                { text: 'Invitations', href: `/organizations/${id}/#` },
                { text: 'Pending Invitations', href: `/organizations/${id}/invitations/pending` }
            ]);
        }

        return () => {
            setBreadcrumbs([]);
        };
    }, [id, organization, setBreadcrumbs]);

    const handleResend = async (invitationId: string) => {
        if (!id) return;
        setLoadingInvitations(prev => ({ ...prev, [invitationId]: true }));
        try {
            await organizationService.resendInvitation(id, invitationId);
            addNotification('success', 'Invitation resent successfully!');
            await fetchInvitations(); // Refresh the list
        } catch (error) {
            addNotification('error', 'Failed to resend invitation. Please try again.');
        } finally {
            setLoadingInvitations(prev => ({ ...prev, [invitationId]: false }));
        }
    };

    if (loading) {
        return (
            <Box padding={{ horizontal: 'm', vertical: 'l' }}>
                <Spinner size="large" />
                <Box color="text-status-info" fontSize="heading-s">
                    Loading invitations...
                </Box>
            </Box>
        );
    }

    if (error || !organization) {
        return (
            <Box padding={{ horizontal: 'm', vertical: 'l' }}>
                <Alert type="error">{error || 'Organization not found'}</Alert>
            </Box>
        );
    }

    return (
        <SpaceBetween size="l">
            <div className='p-5'>
                <Box margin={{ bottom: "m" }}>
                    <div className='pending-invitation-header'>
                        <div className='pending-invitation-title'>
                            <h2>Pending Invitations</h2>
                            <Badge >{invitations.length}</Badge>
                        </div>
                        <Link to={`/organizations/${id}/invite`} state={{ organizationName: organization.display_name }}>
                            <div className='invite-member-button'>
                                <Button
                                    variant="link"
                                >
                                    + Invite new member
                                </Button>
                            </div>
                        </Link>
                    </div>
                </Box>

                <Table
                    columnDefinitions={[
                        {
                            id: 'email',
                            header: 'Email',
                            cell: (item: Invitation) => item.email,
                        },
                        {
                            id: 'createdAt',
                            header: 'Created at',
                            cell: (item: Invitation) => new Date(item.created_at).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            })
                        },
                        {
                            id: 'resend',
                            header: 'Actions',
                            cell: (item: Invitation) => (
                            <Button
                                variant="normal"
                                onClick={() => handleResend(item.id)}
                                loading={loadingInvitations[item.id]}
                            >   
                                <div className='nowarp-text'>
                                    Resend
                                </div>
                            </Button>
                            ),
                        },
                    ]}
                    items={invitations}
                    empty={<Box>No pending invitations.</Box>}
                />
            </div>
        </SpaceBetween>
    );
};

export default PendingInvitationsPage;